import {
  Divider,
  Typography,
  makeStyles,
  AlertDialog,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Hidden,
  IconButton,
  LANGUAGE_ENGLISH_NAMES,
  Scaler,
  Tab,
  Tabs,
  TranslationProvider,
  useIsMobile,
  useBreakpoint
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import {
  Theme as ThemeOverride,
  DialogStackContext,
  DropdownSelect,
  useClientOptions,
  useUser
} from '@paintscout/ui-business'
import type { EditorMethods, DropdownSelectOption } from '@paintscout/ui-business'
import ErrorIcon from '@material-ui/icons/Error'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'
import SendIcon from '@material-ui/icons/Send'
import type { SentEmail } from '@paintscout/api'
import {
  getCustomEmailTemplates,
  getEmailTemplates,
  getFeature,
  getFollowUps,
  getObjectLabels,
  getQuotePresentation,
  getQuoteV1Presentation,
  hasIntegrationInterface,
  isPresentationNext,
  toggleFollowUp
} from '@paintscout/util/builder'
import {
  getTranslationStatusMap,
  type TranslationCompletionStatus
} from '@paintscout/util/builder/quote/util/translation-status'
import { useApolloClient } from '@apollo/react-hooks'
import { scheduleAutomatedDealStageSync } from 'shared/graphql/util/scheduleAutomatedDealStageSync'
import { gql } from 'apollo-boost'
import type { QuoteProps } from '@paintscout/react-quote'
import {
  Presentation,
  PRESENTATION_CONTENT_WIDTHS,
  PresentationNavContextProvider,
  PresentationNavMobile,
  PresentationNextImbedPreview,
  QUOTE_CARD_MAX_WIDTHS,
  QUOTE_PREVIEW_FRAME_WIDTH,
  QuoteContextProvider,
  useQuote
} from '@paintscout/react-quote'
import { useQuoteMutations } from 'shared/hooks'
import { getPostHogClientContext, track } from 'shared/util/analytics'

import { isEmailValid, isPhoneNumberValid } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import { Formik } from 'formik'
import isEqual from 'lodash/isEqual'
import { useSnackbar } from 'notistack'
import type {
  EmailTemplate,
  OptionsDocument,
  PresentationEstimator,
  PresentationOptionV2,
  QuoteDocument
} from 'paintscout'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import SplitterLayout from 'react-splitter-layout'
import * as Yup from 'yup'
import type { SendQuoteFormValues } from './SendQuoteForm'
import SendQuoteForm from './SendQuoteForm'
import { getDefaultEmail } from './getDefaultEmail'
import { translateWorkOrderEmailContentWithLoadingState } from './translateWorkOrderEmailContent'

const WORK_ORDER_VIEW = 'work-order'
const EN = 'en'

/**
 * Send-time analytics. `quote_link_untracked` also fires from QuoteLinkDialog's Preview Link copy,
 * so both ways of bypassing view tracking land on one event. Work orders are excluded because they
 * are always silent (the checkbox isn't offered) — only the deliberate customer-send choice counts.
 */
function trackSend({
  quoteId,
  view,
  silentQuoteLink,
  ...props
}: {
  quoteId: string
  view: string
  silentQuoteLink: boolean
  [key: string]: unknown
}) {
  track('quote_sent', { quoteId, viewType: view, silentQuoteLink, ...props })
  if (silentQuoteLink && view !== WORK_ORDER_VIEW) {
    track('quote_link_untracked', { quoteId, method: 'sent' })
  }
}

// Pre-bake the work order's translations for a language, mirroring WorkOrderViewDialog. The
// in-app preview can't translate dynamic content live (translateNoteContent is customer-view
// only), so the language must be pre-baked onto the quote before the preview can show it.
// `lang` limits the retry to the language picked here. Without it the server retries every
// incomplete language, which pays for languages the company does not use.
export const REFRESH_QUOTE_TRANSLATIONS = gql`
  mutation RefreshQuoteTranslations(
    $quoteId: String!
    $companyId: String!
    $lang: String
    $trigger: String
    $posthogDistinctId: String
    $posthogSessionId: String
  ) {
    refreshQuoteTranslations(
      quoteId: $quoteId
      companyId: $companyId
      lang: $lang
      trigger: $trigger
      posthogDistinctId: $posthogDistinctId
      posthogSessionId: $posthogSessionId
    ) {
      success
      translationStatus {
        lang
        status
      }
    }
  }
`
// Re-fetch the full quote document (JSON scalar) after generating translations so the
// freshly pre-baked quote.translations[lang] is available to render in the preview.
export const QUOTE_QUERY = gql`
  query quote($id: String!) {
    quote(id: $id)
  }
`

export interface SendQuoteDialogProps extends DialogProps {
  loading?: boolean
  view: 'quote' | 'work-order' | 'presentation'
  onConfirm?: () => void
  onCancel?: () => void
}

export const useStyles = makeStyles<Theme, { isV2Preview?: boolean }>((theme: Theme) => ({
  paper: {
    '&:not($paperFullScreen)': {
      height: '80vh',
      maxWidth: '1200px'
    }
  },
  paperFullScreen: {},
  dialogContent: {
    height: '100%',
    paddingTop: 0,
    paddingBottom: 0
  },
  dialogActions: {
    '& > div:first-of-type': {
      // Remove the negative margin so Cancel button aligns with Send button on the right
      marginLeft: '0 !important'
    }
  },
  splitPaneWrapper: {
    position: 'relative',
    height: '100%',
    width: '100%',
    // Column flex so the mobile tab view's preview gets a real bounded height to fill (see
    // presentationPreviewWrapper below) — SplitterLayout's own pane is position:absolute, so it's
    // unaffected by this and still fills splitPaneWrapper on desktop.
    display: 'flex',
    flexDirection: 'column'
  },
  // Bounds the v2 "PresentationNext" preview to its container's real height (the split pane on
  // desktop, this wrapper on mobile) instead of letting `autoHeight` grow the iframe to the
  // document's full height — that made the preview's internal nav/sticky elements size themselves
  // off the (huge) content height instead of the visible viewport (mirrors EditableQuote's fix).
  presentationPreviewWrapper: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  presentationPreviewRoot: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%'
  },
  presentationPreviewMinimalWrapper: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%'
  },
  splitterLayout: (props) => ({
    '& .layout-splitter': {
      background: '#DDDDDD',
      opacity: 0.5,
      zIndex: 1,
      backgroundClip: 'padding-box',
      width: '12px',
      margin: '0 -5px',
      borderLeft: '5px solid rgba(255, 255, 255, 0)',
      borderRight: '5px solid rgba(255, 255, 255, 0)',
      cursor: 'col-resize'
    },
    '& .layout-pane:not(.layout-pane-primary)': {
      // This forked react-splitter-layout ignores any `pane2Style`/`style` prop entirely (see
      // packages/react-splitter-layout/src/components/Pane.jsx) — this class is the only thing
      // that controls the pane's overflow, so it has to be conditional here rather than via a prop.
      // v1's Scaler still wants the always-on 'scroll' (avoids scrollbar flicker on small scrolls);
      // the v2 iframe preview fills the pane exactly and scrolls its own document natively, so a
      // forced scrollbar here would show a track with nothing of its own to scroll.
      overflow: props.isV2Preview ? 'hidden' : 'scroll',
      // A plain block, so a flexed child (presentationPreviewWrapper) can't size itself against
      // it with flex:1 1 auto — making it a flex column gives that child a real height to fill.
      // Harmless for the v1 Scaler pane too: its one child already renders at its natural height.
      display: 'flex',
      flexDirection: 'column'
    }
  }),
  quotePaper: {
    boxShadow: 'none'
  },
  form: {
    paddingRight: theme.spacing(),
    marginRight: theme.spacing()
  },
  secondaryAction: {
    height: theme.typography.pxToRem(50),
    borderTop: '1px #DDDDDD solid',
    margin: 0,
    paddingLeft: theme.typography.pxToRem(0),
    paddingRight: theme.typography.pxToRem(0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tabs: {
    marginLeft: -8,
    marginRight: -8
  },

  presentationWrapper: {
    // The preview's "Menu" AppBar renders `position: static` (see ScaledQuote), so it takes its
    // own vertical space in normal flow — no header-height padding needed to clear a fixed bar.
    pointerEvents: 'none'
  },
  previewToolbar: {
    width: '100%',
    // The shared `toolbar` class has a base `width: 1150` with a down('lg') -> 100% override, so on a
    // >1214px viewport it only loses to the `width: 100%` above by JSS injection order (identical
    // 0,1,0 specificity — the footgun that burned #21732). Cap it so it can't overflow the 980 frame
    // whichever rule wins.
    maxWidth: '100%'
  },
  // The preview's "Menu" bar must line up with the white estimate/work-order/invoice card below it
  // (#17051). The card is Quote.tsx's Paper, which sits inside the presentation content column but is
  // capped independently of it, so its rendered width is min(column, cap) — the bar has to honour BOTH
  // contracts. `width` mirrors the column (PRESENTATION_CONTENT_WIDTHS) and `maxWidth` mirrors the cap
  // (QUOTE_CARD_MAX_WIDTHS); with app breakpoints (sm 790 / md 958 / lg 1214) and MUI v4's EXCLUSIVE
  // down() that resolves to:
  //
  //   viewport         column      card cap      bar & card
  //   > 1213.95        954         954           954
  //   958 - 1213.95    718         818 (inert)   718
  //   <= 957.95        96% (940.8) 818 (binds)   818
  //
  // Matching only the column is what made #21365/#21732/#21743 fail QA (bar 940.8 vs card 818 in the
  // bottom tier = the "landscape fine, portrait/mobile off" fingerprint); matching neither and going
  // full-bleed (#22058) was rejected too. `maxWidth` is repeated in the sm block on purpose — both
  // media queries match below 958px and their relative JSS injection order isn't worth trusting.
  // Stays `position: static` (ScaledQuote passes appBarPosition="static") — position:fixed inside the
  // Scaler's `transform`ed ancestor is resolved inconsistently by iOS Safari/WebKit. With static the
  // bar is in normal flow, the same coordinate space as the document, so both resolve against 980.
  previewToolbarRoot: {
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: PRESENTATION_CONTENT_WIDTHS.default,
    maxWidth: QUOTE_CARD_MAX_WIDTHS.default,
    [theme.breakpoints.down('md')]: {
      width: PRESENTATION_CONTENT_WIDTHS.md,
      maxWidth: QUOTE_CARD_MAX_WIDTHS.md
    },
    [theme.breakpoints.down('sm')]: {
      width: PRESENTATION_CONTENT_WIDTHS.sm,
      // Same cap as the down('md') block above, restated deliberately — NOT a copy-paste slip to
      // clean up. Both media queries match below 958px and neither reliably wins, so whichever one
      // JSS emits last has to carry the correct cap too.
      maxWidth: QUOTE_CARD_MAX_WIDTHS.md
    }
  },
  previewRoot: {
    position: 'relative'
  },
  previewLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: theme.spacing(8),
    background: 'rgba(255, 255, 255, 0.5)',
    zIndex: 2,
    // The overlay only signals progress; clicks should still reach the preview behind it.
    pointerEvents: 'none'
  }
}))

interface Email {
  name: 'To Client' | 'To Staff'
  silentQuoteLink?: boolean
  signature?: string
  values: SendQuoteFormValues
}

const ValidationSchema = Yup.object().shape({
  to: Yup.array().required('Required'),
  sms: Yup.object({
    to: Yup.string().when('enabled', {
      is: true,
      then: Yup.string()
        .test('valid-phone', 'Please enter a valid phone number', (value) => isPhoneNumberValid(value))
        .required(`Please enter a phone number`)
    })
  }),
  body: Yup.string().required('Required'),
  subject: Yup.string().required('Required')
})

/**
 * Resolves the presentation the send-dialog preview should render, and which renderer it needs.
 *
 * Discriminates on the RESOLVED presentation (as EditableQuote's presentation tab does), not the raw
 * quote.presentationTemplate: a v2 template stored without pages falls back to the v1 default inside
 * getQuotePresentation, and that fallback must keep rendering through v1. Not gated on the
 * `presentationNext` flag — viewQuote serves v2 quotes through the v2 renderer ungated, so the
 * preview must match what actually gets sent.
 *
 * Memoized so the resolved doc keeps a stable identity across SendQuoteDialog's per-keystroke
 * re-render (Formik render prop). That covers the common case only — `quote` comes from QuoteContext
 * and takes a fresh identity on some edit flows — so PresentationNextPreview still deep-compares.
 */
function usePreviewPresentation(args: {
  options: OptionsDocument
  quote: QuoteDocument
  view: SendQuoteDialogProps['view']
}) {
  const { options, quote, view } = args
  const presentation = useMemo(
    () => getQuotePresentation({ options, quote, isWorkOrder: view === WORK_ORDER_VIEW }),
    [options, quote, view]
  )

  const isV2 = view !== WORK_ORDER_VIEW && isPresentationNext({ options, presentation })

  // narrowed for the v2 preview, which is the only consumer — the v1 path re-resolves its own copy
  // from the (possibly translation-pre-baked) quote it renders
  return { presentation: isV2 ? (presentation as PresentationOptionV2) : undefined, isV2 }
}

export default function SendQuoteDialog({ view, onConfirm, ...props }: SendQuoteDialogProps) {
  const [fullScreen, setFullScreen] = useState(props.fullScreen)
  const { user, preferences } = useUser()
  const { quote, estimator, save } = useQuote()
  const { options } = useClientOptions()
  const apolloClient = useApolloClient()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { enqueueSnackbar } = useSnackbar()
  const { sendQuote } = useQuoteMutations()
  const [tab, setTab] = useState('email')
  const mobile = useIsMobile()
  const editorRef: React.Ref<EditorMethods> = useRef(null)
  const [bouncedEmailAddresses, setBouncedEmailAddresses] = useState<string[]>([])
  // True while the subject/body are being (re-)translated, whether triggered by the Work
  // Order Language selector (SendQuoteForm) or the Template selector (below) -- shown as a
  // loading overlay over the subject/body fields in SendQuoteForm.
  const [translatingContent, setTranslatingContent] = useState(false)
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = hasStripe || hasStripeW1d || hasPayrix
  const hasSettingsCommunications = getFeature({
    options,
    preferences,
    path: 'settingsCommunications.enabled'
  })

  const quoteLabel = view === WORK_ORDER_VIEW ? objectLabels.workOrder : objectLabels.quote

  const { presentation: previewPresentation, isV2: isV2Preview } = usePreviewPresentation({ options, quote, view })
  const classes = useStyles({ isV2Preview })

  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))
  const supportsTemplateSelection = view === 'quote' || view === 'presentation' || view === WORK_ORDER_VIEW

  const quoteType = quote.int_ext.value

  const baseEmailTemplates = getEmailTemplates({ options, userPreferences: preferences })
  const customEmailTemplateValues = getCustomEmailTemplates({ options, userPreferences: preferences })
  const allEmailTemplates = []
  if (customEmailTemplateValues) {
    Object.keys(customEmailTemplateValues).forEach((key) => allEmailTemplates.push(customEmailTemplateValues[key]))
  }
  allEmailTemplates.push(baseEmailTemplates['send-quote-client'])

  const enabledEmailTemplates = allEmailTemplates.filter((template) => template?.enabled !== false)
  const emailTemplates =
    enabledEmailTemplates.length > 0 ? enabledEmailTemplates : [baseEmailTemplates['send-quote-client']]

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(() => {
    // only loop through for defaults if there is more than one
    if (emailTemplates.length > 1) {
      let i: number
      for (i = 0; i < emailTemplates.length; i++) {
        if (emailTemplates[i].defaultFor?.includes(quoteType)) {
          return i
        }
        if (i === emailTemplates.length - 1) return i
      }
    } else return 0
  })

  const [emails, setEmails] = useState(() => {
    const clientEmailTemplate =
      view === WORK_ORDER_VIEW
        ? baseEmailTemplates['send-work-order-staff']
        : quote.is_invoice
          ? baseEmailTemplates['send-invoice-client']
          : emailTemplates[selectedTemplateIndex]
    const staffEmailTemplateKey = quote.is_invoice
      ? hasSettingsCommunications
        ? 'send-invoice-staff'
        : 'invoice-sent-notification'
      : hasSettingsCommunications
        ? 'send-quote-staff'
        : 'quote-sent-notification'
    const staffEmailTemplate = baseEmailTemplates[staffEmailTemplateKey]

    const followUps = getFollowUps({ preferences, options })

    const quoteType = quote.int_ext.value
    const quoteFollowUps = followUps.order
      .map((key) => followUps.values[key])
      // only show followUps that would apply to this quote, hide all for work-order views
      .filter((followUp) => {
        if (view === WORK_ORDER_VIEW) return null
        else
          return (
            !!quote.is_invoice === !!followUp.isInvoice &&
            (quote.is_invoice ||
              followUp.quoteTypes?.includes(quoteType) ||
              quote.int_ext?.value === 'all' ||
              followUp.quoteTypes?.includes('__all__'))
          )
      })
      .map((followUp) => ({
        ...followUp,
        // a quote-level override wins; with no quote-level state, fall back to the template's sendEmail
        sendEmail:
          typeof quote.followUps?.[followUp.key]?.disabled !== 'undefined'
            ? !quote.followUps[followUp.key].disabled
            : !!followUp.sendEmail,
        sendText: quote.followUps?.[followUp.key]?.sendText ?? followUp.sendText
      }))

    return [
      {
        name: 'To Client',
        enabled: true,
        view,
        silentQuoteLink: view === WORK_ORDER_VIEW,
        signature: clientEmailTemplate.signature,
        values: {
          ...getDefaultEmail({
            user,
            quote,
            options,
            autofillTo: view === 'quote' || view === 'presentation',
            emailTemplate: clientEmailTemplate
          }),
          // only enable follow ups for 'To Client' emails
          view: view,
          followUps: quoteFollowUps,
          followUpsDisabled: quote.followUpsDisabled
        }
      },
      view === 'quote' && staffEmailTemplate?.enabled && options.options.features?.quotes?.emails?.toStaff
        ? {
            name: 'To Staff',
            view: 'quote',
            silentQuoteLink: true,
            signature: staffEmailTemplate.signature,
            enabled: true,
            values: {
              ...getDefaultEmail({
                user,
                quote,
                options,
                autofillTo: false,
                emailTemplate: staffEmailTemplate
              }),
              view: 'quote'
            }
          }
        : null
    ].filter((x) => !!x) as Email[]
  })

  const [selectedEmailIndex, setSelectedEmailIndex] = useState(0)

  // The raw (not-yet-templated) content currently backing the work-order "To Client" email, so the
  // Work Order Language selector (SendQuoteForm) can live-translate whichever template the user
  // actually picked via templateSelector rather than always re-deriving the fixed
  // 'send-work-order-staff' default. Undefined outside work-order view, where it's never read.
  const [selectedWorkOrderEmailTemplate, setSelectedWorkOrderEmailTemplate] = useState<EmailTemplate | undefined>(() =>
    view === WORK_ORDER_VIEW ? baseEmailTemplates['send-work-order-staff'] : undefined
  )

  const hasSMS = options.options?.features?.sms?.enabled ?? false
  const defaultTextSetting = options.options?.quotes?.sendQuoteByTextDefault ?? false
  const initialValues = {
    ...emails[selectedEmailIndex].values,
    sms: {
      ...emails[selectedEmailIndex].values.sms,
      enabled: !hasSMS ? false : defaultTextSetting ? true : emails[selectedEmailIndex].values.sms.enabled
    }
  }

  const handleBounceAlertMessage = (bouncedEmails: SentEmail[], sentEmails: SentEmail[]) => {
    const message = []
    // bounced
    message.push(
      <Typography key={'typography0'} variant="h3">
        Unable to Send to:{' '}
      </Typography>
    )
    message.push(<br key={'br0'} />)
    bouncedEmails.map((email) =>
      message.push(
        <Typography variant="body1" key={email.headers.to}>
          <strong>{email.headers.to}</strong>
        </Typography>
      )
    )
    message.push(<br key={'br1'} />)
    message.push(
      <Typography key={'typography1'} variant="body1" component="div">
        Please ensure you entered the above email address(es) correctly.
      </Typography>
    )
    // sent
    if (sentEmails.length) {
      message.push(<br key={'br2'} />)
      message.push(<Divider key={'div0'} />)
      message.push(<br key={'br3'} />)
      message.push(
        <Typography key={'typography2'} variant="body1">
          Successfully Sent to:
        </Typography>
      )
      message.push(<br key={'br4'} />)
      sentEmails.map((email) =>
        message.push(
          <Typography variant="body1" component="div" key={email.response.To}>
            {email.response.To}
          </Typography>
        )
      )
    }
    return message
  }

  return (
    <Dialog
      classes={{ paper: classes.paper, paperFullScreen: classes.paperFullScreen }}
      open={props.open}
      onClose={props.onCancel}
      forceFullScreen={mobile ? true : fullScreen}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
    >
      <DialogTitle
        loading={props.loading}
        rightContent={
          // implementation="css" will prevent error on rightContent animation when hidden
          <Hidden smDown={true} implementation="css">
            <IconButton onClick={() => setFullScreen(!fullScreen)}>
              {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Hidden>
        }
      >
        Review &amp; Send
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values) => {
          const selectedEmail = {
            ...emails[selectedEmailIndex],
            values
          }
          try {
            // update toggled followUps, only writing state for follow-ups that changed
            const updatedQuote: QuoteDocument = (values.followUps ?? []).reduce(
              (quote, followUp) => {
                const existingFollowUp = quote?.followUps?.[followUp.key]
                if (
                  existingFollowUp?.disabled !== !followUp.sendEmail ||
                  existingFollowUp?.sendText !== followUp.sendText
                ) {
                  return toggleFollowUp({
                    quote,
                    key: followUp.key,
                    enabled: followUp.sendEmail,
                    sendText: followUp.sendText
                  })
                }
                return quote
              },
              { ...quote, followUpsDisabled: values.followUpsDisabled }
            )
            // Save before sending, dont send if save fails
            const savedQuote = await save(updatedQuote)
            const validEmails = (values?.to ?? []).filter((email) => isEmailValid(email))
            if (!savedQuote) {
              console.log('Failed to save quote:', updatedQuote)
              console.log('res', savedQuote)
              Sentry.captureException(new Error('Failed to save quote before send'), { tags: { quoteId: quote._id } })
              enqueueSnackbar(
                `Unable to save ${objectLabels.quote.value.toLowerCase()} before sending. ${objectLabels.quote.value.toLowerCase()} was not sent.`,
                {
                  variant: 'error'
                }
              )
            } else if (
              selectedEmail?.name != 'To Staff' &&
              savedQuote?.status?.history?.sent?.timestamp &&
              savedQuote?.status?.history?.sent?.timestamp > Date.now() - 2 * 60_000 &&
              50 > 75
            ) {
              enqueueSnackbar(
                "It looks like this quote was successfully sent very recently. To prevent spam, you'll be able to send again after 2 minutes.",
                { variant: 'warning' }
              )
            } else if (!validEmails.length && hasPayments && view !== WORK_ORDER_VIEW) {
              enqueueSnackbar("Contact email is invalid. Please update the contact's email address and try again.", {
                variant: 'error'
              })
            } else {
              const silentQuoteLink = values.silentQuoteLink ?? selectedEmail.silentQuoteLink ?? false
              const sendQuoteResponse = await sendQuote(quote._id, {
                emails: [
                  {
                    to: values.to,
                    from: values.from,
                    subject: values.subject,
                    body: values.body,
                    sms: values.sms?.enabled
                      ? {
                          to: [values.sms.to]
                        }
                      : null,
                    ...(values.reminderOptions?.enabled ? { reminderOptions: values.reminderOptions } : {}),
                    attachPdf: values.attachPdf,
                    silentQuoteLink,
                    signature: selectedEmail.signature,
                    // Only set for work-order sends with translations enabled (selector gated in SendQuoteForm).
                    ...(values.lang ? { lang: values.lang } : {})
                  }
                ],
                view
              })

              trackSend({
                quoteId: quote._id,
                view,
                silentQuoteLink,
                isInvoice: !!quote.is_invoice,
                recipientCount: values.to?.length,
                smsEnabled: values.sms?.enabled ?? false,
                attachPdf: values.attachPdf ?? false,
                emailName: selectedEmail.name
              })

              // Sending can move the deal's stage through an automation that runs in a separate
              // Lambda, long after this mutation resolves — see scheduleAutomatedDealStageSync for
              // why this needs several attempts. A work-order send emits no status change. (#22137)
              if (quote.deal_id && view !== WORK_ORDER_VIEW) scheduleAutomatedDealStageSync(apolloClient)

              const sentEmails = sendQuoteResponse.data.sendQuote?.emails ?? []
              const sentBouncedEmails = sentEmails.filter((email) => email.response.error === true)
              const sentSuccessEmails = sentEmails.filter((email) => email.response.ErrorCode === 0)
              if (!sentBouncedEmails.length) {
                onConfirm()
                enqueueSnackbar('Sent', { id: 'toast-quote-sent', variant: 'success' })
              } else {
                setBouncedEmailAddresses(sentBouncedEmails.map((email) => email.headers.to))
                openDialog(AlertDialog, {
                  icon: <ErrorIcon />,
                  title: `Unable to Send ${objectLabels.quote.value} to All Recipients`,
                  onConfirm: dismissDialog,
                  message: handleBounceAlertMessage(sentBouncedEmails, sentSuccessEmails)
                })
              }
            }
          } catch (error) {
            Sentry.captureException(error, { tags: { quoteId: quote._id } })
            const message = error?.graphQLErrors?.[0]?.message || 'Unable to send'
            enqueueSnackbar(message, { variant: 'error' })
          }
        }}
      >
        {({ values, handleSubmit, isSubmitting }) => {
          // v2 "PresentationNext" presentations render in the v2 iframe preview, which needs none of
          // the v1 path's translation machinery (that's work-order-only, and work orders are always
          // the v1 default presentation). The v1 preview mirrors the Work Order Language selector:
          // when a non-English language is chosen, the work order is pre-baked + re-fetched so the
          // preview renders in that language. English shows the original immediately.
          const preview = isV2Preview ? (
            <div className={classes.presentationPreviewWrapper}>
              <PresentationNextPreview
                presentation={previewPresentation}
                classes={{
                  root: classes.presentationPreviewRoot,
                  minimalWrapper: classes.presentationPreviewMinimalWrapper
                }}
              />
            </div>
          ) : (
            <TranslatedQuotePreview
              quote={quote}
              estimator={estimator}
              view={view}
              lang={values.lang}
              classes={classes}
            />
          )

          // put rendered form into a variable so we can take it in/out of the split view
          // when mobile
          const form = (
            <div className={classes.form}>
              <SendQuoteForm
                bouncedEmailAddresses={bouncedEmailAddresses.length ? bouncedEmailAddresses : null}
                id="send-quote-form"
                editorRef={editorRef}
                data-testid="send-quote-form"
                disabled={isSubmitting}
                emailName={emails[selectedEmailIndex].name}
                workOrderEmailTemplate={selectedWorkOrderEmailTemplate}
                translating={translatingContent}
                onTranslatingChange={setTranslatingContent}
                emailSelector={
                  emails.length > 1 && (
                    <DropdownSelect
                      label="Email"
                      margin="dense"
                      variant="single"
                      disabled={isSubmitting}
                      fullWidth
                      value={{
                        value: selectedEmailIndex,
                        label: emails[selectedEmailIndex].name
                      }}
                      options={emails.map((email, index) => ({
                        label: email.name,
                        value: index
                      }))}
                      onChange={(option: DropdownSelectOption) => {
                        const newIndex = option.value

                        // update current email if changing template
                        setEmails([
                          ...emails.slice(0, selectedEmailIndex),
                          {
                            ...emails[selectedEmailIndex],
                            values
                          },
                          ...emails.slice(selectedEmailIndex + 1)
                        ])

                        setSelectedEmailIndex(newIndex)
                      }}
                    />
                  )
                }
                templateSelector={
                  supportsTemplateSelection &&
                  !quote.is_invoice &&
                  emailTemplates.length > 1 &&
                  emails[selectedEmailIndex].name === 'To Client' && (
                    <DropdownSelect
                      label="Template"
                      margin="dense"
                      variant="single"
                      disabled={isSubmitting}
                      fullWidth
                      value={{
                        label: emailTemplates[selectedTemplateIndex]?.label,
                        value: selectedTemplateIndex
                      }}
                      options={emailTemplates.map((template, index) => ({
                        label: `${template.label}${
                          template.defaultFor?.includes(quoteType) || template.defaultFor?.includes('all')
                            ? ' - Default'
                            : ''
                        }`,
                        value: index
                      }))}
                      onChange={async (option: DropdownSelectOption) => {
                        const newIndex = option.value
                        const newValues = getDefaultEmail({
                          user,
                          quote,
                          options,
                          autofillTo: true,
                          emailTemplate: emailTemplates[newIndex]
                        })
                        // A language was already selected (Work Order Language) -- translate the
                        // newly selected template's content too, live, instead of leaving it in
                        // English until the language selector is touched again.
                        const { subject, body } = await translateWorkOrderEmailContentWithLoadingState({
                          apolloClient,
                          subject: newValues.subject,
                          body: newValues.body,
                          lang: values.lang,
                          setLoading: setTranslatingContent
                        })

                        // update current email if changing template
                        // followUps are by quote type and so do not need to be recreated/updated on template change
                        setEmails([
                          ...emails.slice(0, selectedEmailIndex),
                          {
                            ...emails[selectedEmailIndex],
                            values: {
                              ...newValues,
                              subject,
                              body,
                              // getDefaultEmail only returns template content (to/from/subject/body/
                              // sms/attachPdf/signature). Preserve the user-set send context it omits
                              // so a template switch doesn't clobber it — same precedent as followUps:
                              //  - view/lang gate the Work Order Language selector
                              //  - silentQuoteLink is the "Send Without View Tracking" choice
                              //  - reminderOptions are the configured send reminders
                              view: values.view,
                              lang: values.lang,
                              silentQuoteLink: values.silentQuoteLink,
                              reminderOptions: values.reminderOptions,
                              followUps: values.followUps,
                              followUpsDisabled: values.followUpsDisabled
                            }
                          },
                          ...emails.slice(selectedEmailIndex + 1)
                        ])
                        editorRef.current.setHTML(body)
                        setSelectedTemplateIndex(newIndex)
                        setSelectedWorkOrderEmailTemplate(emailTemplates[newIndex])
                      }}
                    />
                  )
                }
              />
            </div>
          )

          return (
            <>
              <DialogContent classes={{ root: classes.dialogContent }}>
                <div className={classes.splitPaneWrapper}>
                  {isSmallScreen ? (
                    <>
                      <Tabs
                        value={tab}
                        variant="fullWidth"
                        className={classes.tabs}
                        onChange={(ev, newTab) => setTab(newTab)}
                      >
                        <Tab value="email" label="Email" />
                        <Tab value="quote" label={quoteLabel.value} />
                      </Tabs>
                      {tab === 'email' && form}
                      {tab === 'quote' && preview}
                    </>
                  ) : (
                    <SplitterLayout
                      customClassName={classes.splitterLayout}
                      percentage={true}
                      primaryMinSize={30}
                      secondaryMinSize={30}
                    >
                      {form}
                      {preview}
                    </SplitterLayout>
                  )}
                </div>
              </DialogContent>
              <DialogActions
                className={classes.dialogActions}
                alignLeftButtonWithTitle={false}
                leftButton={
                  <Button onClick={props.onCancel} variant="textNoPadding" disabled={isSubmitting}>
                    Cancel
                  </Button>
                }
              >
                <Button
                  icon={SendIcon}
                  variant={'contained'}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {`Send ${quoteLabel.value}`}
                </Button>
              </DialogActions>
            </>
          )
        }}
      </Formik>
    </Dialog>
  )
}

/**
 * The send-dialog preview for a v2 "PresentationNext" presentation: the same iframe-based
 * customer-view preview the estimate's presentation tab uses (see EditableQuote), so the estimator
 * reviews exactly what the recipient will open. The caller bounds it to a real height (the split
 * pane on desktop, `presentationPreviewWrapper` on mobile) via `classes` so the iframe fills the
 * visible preview area and scrolls its own content natively, rather than `autoHeight` growing it to
 * the document's full height; `hideFooter` and the absent `handleAcceptClick` keep accept/response
 * UI out of a send preview.
 *
 * Quote/estimator come from QuoteContext inside the preview, so only the presentation is passed.
 */
// Memoized (deep, like ScaledQuote below) so a keystroke in the send form doesn't reconcile the
// whole preview. usePreviewPresentation's useMemo is not sufficient on its own: it keys on the
// `quote` from QuoteContext, which takes a fresh object identity on some edit flows, so the resolved
// presentation can be a new-but-equal object. A deep compare of the page/section tree is much
// cheaper than re-rendering the iframe'd customer view, so it wins over a reference compare here.
export const PresentationNextPreview = React.memo(function PresentationNextPreview({
  presentation,
  classes
}: {
  presentation: PresentationOptionV2
  classes?: { root?: string; minimalWrapper?: string }
}) {
  return (
    <PresentationNextImbedPreview
      border={'none'}
      loadingStyle={'cv'}
      hideFooter
      presentation={presentation}
      usePresentationFromProp
      classes={classes}
    />
  )
}, isEqual)

// prevents re-render on every keystroke by using isEqual to compare props
export const ScaledQuote = React.memo(function ScaledQuote(
  props: QuoteProps & { classes: any; estimator: PresentationEstimator }
) {
  const { quote, estimator, classes, view } = props
  const [currentPage, setCurrentPage] = useState(null)
  const [navOpen, setNavOpen] = useState(false)
  const { options } = useClientOptions()
  const { wisetackPromo } = useQuote()
  // Deliberately re-resolved here rather than taking the dialog's memoized `previewPresentation`:
  // the `quote` prop may be the re-fetched, translation-pre-baked copy TranslatedQuotePreview swaps
  // in, and the presentation must be derived from the quote actually being rendered.
  const presentation = getQuoteV1Presentation({
    options,
    quote,
    isWorkOrder: view === WORK_ORDER_VIEW
  })

  return (
    <Scaler fixedWidth={QUOTE_PREVIEW_FRAME_WIDTH}>
      <QuoteContextProvider quote={quote} estimator={estimator} contextValue={{ wisetackPromo, isPresentation: true }}>
        <PresentationNavContextProvider
          open={navOpen}
          currentPage={currentPage}
          view={view}
          popupMenu={true}
          onAcceptClick={() => {}}
          onPageClick={handleNavClick}
          onOpen={() => setNavOpen(true)}
          onClose={() => setNavOpen(false)}
        >
          <PresentationNavMobile
            appBarPosition="static"
            classes={{ toolbar: classes.previewToolbar, appBar: classes.previewToolbarRoot }}
          />
          <div className={classes.presentationWrapper}>
            <Presentation presentation={presentation} classes={{}} page={currentPage} />
          </div>
        </PresentationNavContextProvider>
      </QuoteContextProvider>
    </Scaler>
  )

  function handleNavClick(ev: React.MouseEvent, value: string) {
    setCurrentPage(value)
    setNavOpen(false)
  }
}, isEqual)

/**
 * Renders the send-dialog quote preview, translated to match the Work Order Language
 * selector. Static labels translate immediately via TranslationProvider; dynamic
 * content (item names, notes) needs quote.translations[lang] pre-baked — and the in-app
 * preview can't translate it live (translateNoteContent is customer-view only) — so a
 * not-yet-generated language is pre-baked + re-fetched first, showing a spinner meanwhile.
 * English (the default) shows the original quote with no generation.
 */
// Memoized so a parent re-render (a keystroke in the send form) doesn't reconcile the
// preview: every prop here is a stable reference except `lang`, which only changes when
// the Work Order Language selector changes.
export const TranslatedQuotePreview = React.memo(function TranslatedQuotePreview({
  quote,
  estimator,
  view,
  lang,
  classes
}: {
  quote: QuoteDocument
  estimator: PresentationEstimator
  view: SendQuoteDialogProps['view']
  lang?: string
  classes: ReturnType<typeof useStyles>
}) {
  const apolloClient = useApolloClient()
  const { options, clientId } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  // The quote rendered in the preview. Swapped for a re-fetched copy once translations are
  // pre-baked so the inline work order can render the newly available quote.translations[lang].
  const [displayQuote, setDisplayQuote] = useState<QuoteDocument>(quote)
  const [generating, setGenerating] = useState(false)
  // Languages already pre-baked during this dialog session, so toggling back to one is instant.
  const ensuredLangs = useRef<Set<string>>(new Set([EN]))

  // ScaledQuote is memoized with isEqual; keep a stable classes reference so a parent
  // re-render (e.g. a keystroke in the form) doesn't bust its memo and re-render the quote.
  const previewClasses = useMemo(() => ({ ...classes, paper: classes.quotePaper }), [classes])

  // The generation effect keys only on the language (and view + quote identity) — the other
  // inputs it reads (the quote document, options, clientId, the apollo client, the snackbar)
  // must NOT re-trigger generation, or an unstable reference would re-fire the mutation. Hold
  // them in a ref so the effect always reads the latest values without listing them as deps.
  const genInputs = useRef({ quote, options, clientId, apolloClient, enqueueSnackbar })
  genInputs.current = { quote, options, clientId, apolloClient, enqueueSnackbar }
  const quoteId = quote._id

  // A new quote document (different _id) invalidates everything pre-baked this session: reset
  // the displayed quote and the ensured-language set so its translations re-generate/re-fetch.
  useEffect(() => {
    setDisplayQuote(genInputs.current.quote)
    ensuredLangs.current = new Set([EN])
  }, [quoteId])

  useEffect(() => {
    const { quote, options, clientId, apolloClient, enqueueSnackbar } = genInputs.current
    // English (and "no language") shows the original quote with no generation.
    if (!lang || lang === EN || view !== WORK_ORDER_VIEW) {
      return
    }
    if (ensuredLangs.current.has(lang)) {
      return
    }
    // Already complete on the quote we hold — render it directly, no regenerate/re-fetch.
    if (getTranslationStatusMap({ quote, options, langs: [lang] })[lang] === 'complete') {
      ensuredLangs.current.add(lang)
      return
    }

    let cancelled = false
    setGenerating(true)
    ;(async () => {
      try {
        // Read at call time, not render time: the session id rolls over after 30 minutes idle,
        // and a stale one would file the event under a replay that already ended.
        const postHogContext = getPostHogClientContext()
        const { data } = await apolloClient.mutate<{
          refreshQuoteTranslations?: {
            success: boolean
            translationStatus?: Array<{ lang: string; status: TranslationCompletionStatus } | null> | null
          } | null
        }>({
          mutation: REFRESH_QUOTE_TRANSLATIONS,
          // The posthog* pair is analytics attribution only: the generation event is emitted
          // server-side, and these are what tie it to this user and this session's replay
          // (ENG-766).
          variables: {
            quoteId,
            companyId: clientId,
            lang,
            trigger: 'send-dialog',
            posthogDistinctId: postHogContext.distinctId,
            posthogSessionId: postHogContext.sessionId
          }
        })

        const result = data?.refreshQuoteTranslations
        const langStatus = result?.translationStatus?.find((entry) => entry?.lang === lang)?.status
        if (!result?.success || langStatus !== 'complete') {
          throw new Error(`refreshQuoteTranslations did not complete for "${lang}"`)
        }

        // No work_order_translation_generated event here: the mutation above already reported
        // it server-side, from the point the content was generated, so every trigger counts
        // once — including the ones no client can see (the customer-view work-order link and
        // background save pre-bakes). See applyQuoteTranslations/trackTranslationsGenerated
        // (ENG-766). Firing here too would double-count this path.
        const refetched = await apolloClient.query<{ quote?: QuoteDocument }>({
          query: QUOTE_QUERY,
          variables: { id: quoteId },
          fetchPolicy: 'network-only'
        })
        if (cancelled) {
          return
        }
        // QUOTE_QUERY returns a JSON scalar with no shape guarantee. Only swap in something
        // that looks like a real quote document (has an _id); otherwise a transformed/partial
        // response could corrupt the rendered preview. Fall back to the original on a bad shape.
        const updated = refetched?.data?.quote
        if (!updated?._id) {
          throw new Error(`quote re-fetch returned an unexpected shape for "${lang}"`)
        }
        setDisplayQuote(updated)
        ensuredLangs.current.add(lang)
      } catch (err) {
        if (cancelled) {
          return
        }
        Sentry.captureException(err, { tags: { source: 'SendQuoteDialog.TranslatedQuotePreview', lang } })
        // Best-effort preview only: the recipient's link is translated server-side on view,
        // so a failed in-app pre-bake shouldn't block sending — surface it and fall back to
        // the original. Static labels still translate via TranslationProvider below.
        enqueueSnackbar(
          `Unable to preview the ${LANGUAGE_ENGLISH_NAMES[lang] ?? lang} translation. It will still be translated when the recipient opens it.`,
          { variant: 'warning' }
        )
      } finally {
        if (!cancelled) {
          setGenerating(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [lang, view, quoteId])

  return (
    <div className={classes.previewRoot}>
      {generating && (
        <div className={classes.previewLoadingOverlay} data-testid="send-quote-preview-loading">
          <CircularProgress size={32} />
        </div>
      )}
      <ThemeOverride customerFacing>
        <TranslationProvider lang={lang ?? EN}>
          <ScaledQuote quote={displayQuote} estimator={estimator} view={view} classes={previewClasses} />
        </TranslationProvider>
      </ThemeOverride>
    </div>
  )
})
